import { useState } from 'react';
import { toast } from 'react-toastify';
import { productoService } from '../../services/productoService';

interface ImportCvaModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const ImportCvaModal = ({ onClose, onSuccess }: ImportCvaModalProps) => {
  const [url, setUrl] = useState('https://www.grupocva.com/catalogo_clientes_xml/lista_precios.xml?cliente=46555&marca=HP&grupo=%&clave=%&codigo=%');
  const [marca, setMarca] = useState('HP');
  const [grupo, setGrupo] = useState('');
  const [porcentaje, setPorcentaje] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [createCategories, setCreateCategories] = useState(true);
  const [incluirPromos, setIncluirPromos] = useState(false);
  const [incluirDescripciones, setIncluirDescripciones] = useState(false);
  const [incluirSucursales, setIncluirSucursales] = useState(false);
  const [incluirDepto, setIncluirDepto] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState<null | { 
    created: number; 
    updated: number; 
    totalProcessed: number;
    errors: Array<{ row: number; message: string }> 
  }>(null);

  const handleSubmit = async () => {
    if (!url) {
      toast.error('La URL es requerida');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await productoService.importCva(url, { 
        dryRun, 
        createCategories,
        marca: marca || undefined,
        grupo: grupo || undefined,
        porcentaje: porcentaje ? parseFloat(porcentaje) : undefined,
        incluirPromos,
        incluirDescripciones,
        incluirSucursales,
        incluirDepto
      });
      setPreview(res.data);
      if (!dryRun) {
        toast.success(
          `Importación completada: ${res.data.created} creados, ${res.data.updated} actualizados de ${res.data.totalProcessed} procesados`
        );
        onSuccess();
        onClose();
      } else {
        toast.info('Simulación completada. Revisa los resultados y ejecuta sin simulación.');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Error al importar desde CVA');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Importar Productos desde CVA</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL del Catálogo XML
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="https://www.grupocva.com/catalogo_clientes_xml/lista_precios.xml?cliente=..."
            />
            <p className="text-xs text-gray-500 mt-1">
              URL del catálogo XML de CVA. Ejemplo: lista_precios.xml?cliente=46555&marca=HP
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca (opcional)
              </label>
              <input
                type="text"
                value={marca}
                onChange={(e) => setMarca(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="HP"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se usará para crear categoría si no existe
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grupo/Categoría (opcional)
              </label>
              <input
                type="text"
                value={grupo}
                onChange={(e) => setGrupo(e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Laptops"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se usará para crear categoría si no existe
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Porcentaje de aumento de precio (opcional)
            </label>
            <input
              type="number"
              value={porcentaje}
              onChange={(e) => setPorcentaje(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="10"
              min="0"
              step="0.01"
            />
            <p className="text-xs text-gray-500 mt-1">
              Aumenta el precio de CVA en el porcentaje especificado (ej: 10 = 10%)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={incluirPromos} 
                onChange={(e) => setIncluirPromos(e.target.checked)} 
                className="rounded"
              />
              <span className="text-sm">Incluir promociones</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={incluirDescripciones} 
                onChange={(e) => setIncluirDescripciones(e.target.checked)} 
                className="rounded"
              />
              <span className="text-sm">Incluir descripciones técnicas/comerciales</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={incluirSucursales} 
                onChange={(e) => setIncluirSucursales(e.target.checked)} 
                className="rounded"
              />
              <span className="text-sm">Incluir información de sucursales</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={incluirDepto} 
                onChange={(e) => setIncluirDepto(e.target.checked)} 
                className="rounded"
              />
              <span className="text-sm">Incluir clasificación de departamento</span>
            </label>
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                checked={dryRun} 
                onChange={(e) => setDryRun(e.target.checked)} 
                className="rounded"
              />
              <span className="text-sm">Simulación (dry-run)</span>
            </label>
            <label className="flex items-center space-x-2">
              <input 
                type="checkbox" 
                checked={createCategories} 
                onChange={(e) => setCreateCategories(e.target.checked)} 
                className="rounded"
              />
              <span className="text-sm">Crear categorías si no existen</span>
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> El sistema intentará mapear automáticamente los campos del XML 
              (código, nombre, precio, marca, grupo, etc.) a los productos. Si un producto ya existe 
              (mismo SKU), se actualizará. Si no existe, se creará.
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <button 
              onClick={onClose} 
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={isSubmitting || !url} 
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Procesando...' : (dryRun ? 'Simular Importación' : 'Importar Productos')}
            </button>
          </div>

          {preview && (
            <div className="mt-4 border-t pt-4">
              <div className="mb-2">
                <h3 className="font-semibold text-gray-900 mb-2">Resultados de la Importación</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Procesados:</span>
                    <strong className="ml-2 text-gray-900">{preview.totalProcessed}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">Creados:</span>
                    <strong className="ml-2 text-green-600">{preview.created}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">Actualizados:</span>
                    <strong className="ml-2 text-blue-600">{preview.updated}</strong>
                  </div>
                </div>
              </div>
              {preview.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-red-600 mb-2">
                    Errores ({preview.errors.length})
                  </h4>
                  <div className="max-h-64 overflow-y-auto border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Fila</th>
                          <th className="px-3 py-2 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {preview.errors.map((err, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2">{err.row}</td>
                            <td className="px-3 py-2 text-red-600">{err.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportCvaModal;

